import '../styles/globals.css';
import { mergeDeepRight } from 'ramda';
import React, { useState, useEffect, useRef } from 'react';
import type { AppProps } from 'next/app';
import { ThemeProvider } from 'styled-components';
import getTheme from '../src/theme';
import useWidgetContext from '../src/hooks/useWidgetContext';
import WidgetContext, { IWidgetContext } from '../src/context/WidgetContext';
import windowConfigToWidgetContext from '../src/utils/windowConfigToWidgetContext';
import Layout from '../src/components/Layout';
import useMessaging from '../src/hooks/useMessaging';
import { WindowConfig } from '../src/shared/types';
import StateProvider from '../src/components/StateProvider';
import HeightContext from '../src/context/HeightContext';
import Script from 'next/script';

const App: React.FC<AppProps> = ({ Component, pageProps }) => {
  const headerRef = useRef<HTMLHRElement | null>(null);
  const footerRef = useRef<HTMLHRElement | null>(null);
  const widgetContext = useWidgetContext();

  // Only render app when widget is ready
  return (
    <HeightContext.Provider value={{ footerRef, headerRef }}>
      <ThemeProvider theme={getTheme(widgetContext.styleConfig)}>
        <StateProvider>
          <Layout>
            <Component {...pageProps} />
          </Layout>
        </StateProvider>
      </ThemeProvider>
    </HeightContext.Provider>
  );
};

const WithConfig: React.FC<AppProps & { children: React.ReactNode }> = ({
  children,
  ...props
}) => {
  const [widgetContext, setWidgetContext] = useState<IWidgetContext | null>(
    null,
  );

  const sendMessage = useMessaging(message => {
    if (message.type === 'overwriteConfig') {
      // Only needed for TS, the config object should be there.
      const newConfig =
        window.__DH_CONFIG != null
          ? (mergeDeepRight<WindowConfig, Partial<WindowConfig>>(
              window.__DH_CONFIG,
              message.payload,
            ) as WindowConfig)
          : window.__DH_CONFIG;

      // Also set on window
      window.__DH_CONFIG = newConfig;

      setWidgetContext(
        windowConfigToWidgetContext(newConfig, widgetContext?.hostPageParams),
      );
    }
  });

  useEffect(() => {
    const search = global.window.location.search;
    setWidgetContext(windowConfigToWidgetContext(window.__DH_CONFIG, search));

    // eslint-disable-next-line no-console
    if (window.__DH_CONFIG?.apps.length === 0 && console && console.warn) {
      // eslint-disable-next-line no-console
      console.warn(
        `[DatHuis Widget] No apps enabled, please enable some apps.`,
      );
    }
  }, []);

  useEffect(() => {
    if (widgetContext !== null) {
      sendMessage({
        type: 'configLoaded',
        payload: window.__DH_CONFIG,
      });
    }
  }, [sendMessage, widgetContext]);

  if (widgetContext === null || Object.keys(widgetContext?.apps).length === 0)
    return null;

  return (
    <WidgetContext.Provider value={widgetContext}>
      <Script
        async
        src={`https://www.googletagmanager.com/gtag/js?id=${process.env.GA_ID}`}
      />
      <Script
        id="gtag_script"
        dangerouslySetInnerHTML={{
          __html: `
  window.dataLayer = window.dataLayer || [];
  function gtag(){dataLayer.push(arguments);}
  gtag('js', new Date());

  gtag('config', '${process.env.GA_ID}', {
    'cookie_flags': 'max-age=7200;secure;samesite=none',
    'anonymize_ip': true,
    'send_page_view': false,
  });
`,
        }}
      />
      <App {...props} />
      {children}
    </WidgetContext.Provider>
  );
};

export default WithConfig;
